"use client";

import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import Swal from "sweetalert2";
import { useTranslations } from "next-intl";

interface Props {
  onTimeout: () => void;
  isActive: boolean;
  timeoutSeconds?: number;
  expiresAt?: string | null;
  timerLabel: string;
  timeoutTitle: string;
  timeoutMessage: string;
  confirmButtonText: string;
}

export default function BookingSessionTimer({
  onTimeout,
  isActive,
  timeoutSeconds = 15 * 60,
  expiresAt,
  timerLabel,
  timeoutTitle,
  timeoutMessage,
  confirmButtonText,
}: Props) {
  const [timeLeft, setTimeLeft] = useState(timeoutSeconds);

  useEffect(() => {
    if (expiresAt) {
      const expiry = new Date(expiresAt).getTime();
      const now = new Date().getTime();
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setTimeLeft(diff);
    } else {
      setTimeLeft(timeoutSeconds);
    }
  }, [expiresAt, timeoutSeconds]);

  useEffect(() => {
    if (!isActive || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSessionTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, timeLeft > 0]);

  const handleSessionTimeout = () => {
    Swal.fire({
      title: timeoutTitle,
      text: timeoutMessage,
      icon: "warning",
      confirmButtonText: confirmButtonText,
      confirmButtonColor: "var(--primary)",
      allowOutsideClick: false,
    }).then(() => {
      setTimeLeft(timeoutSeconds);
      onTimeout();
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isActive) return null;

  return (
    <>
      {/* Mobile spacer so page content starts below the fixed timer bar */}
      <div className="h-10 w-full md:hidden" aria-hidden="true" />

      <div className="fixed top-[63px] inset-x-0 z-40 md:top-auto md:bottom-6 md:right-6 md:inset-x-auto md:z-50 flex justify-center md:justify-end animate-in slide-in-from-top-2 md:slide-in-from-bottom-5 fade-in duration-300">
        <div
          className={`w-full md:w-[250px] flex items-center justify-center gap-3 rounded-none md:rounded-full px-4 py-2 md:px-5 md:py-2 border-t-0 border-x-0 border-b md:border-2 shadow-md md:shadow-2xl transition-all ${
            timeLeft < 60
              ? "border-danger bg-danger/10 text-danger animate-pulse shadow-danger/20"
              : "border-border md:border-secondary/30 bg-card text-primary"
          }`}
        >
          <Clock
            size={18}
            className={timeLeft < 60 ? "text-danger" : "text-secondary"}
          />
          <div className="flex flex-row items-center">
            <span className="text-[12px] font-black uppercase tracking-wider opacity-60">
              {timerLabel}
            </span>
            <span className="text-[16px] font-black tabular-nums leading-none pt-[2px] px-3">
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
